import { isEqual, map, omit, pick, size, some, zip } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@apollo/client';

import { useAccount } from '../../hooks/data/auth';
import { shipmentConversions } from '../../app/data/shipmentConversions';
import { QUOTE_DETAIL_QUERY } from '../../app/graphql/quoteQueries';

const werePackagesChanged = (quotePackages, currentShipmentPackages) => {
  const packageCompareFields = [
    'actualWeight',
    'height',
    'length',
    'width',
    'packageSource.code',
    'packagingType.code',
  ];
  if (size(quotePackages) !== size(currentShipmentPackages)) return true;

  const differences = map(
    zip(quotePackages, currentShipmentPackages),
    pack =>
      !isEqual(
        pick(pack[0], packageCompareFields),
        pick(pack[1], packageCompareFields)
      )
  );
  return some(differences);
};

const wasShipmentChanged = (quoteData, currentShipmentData) => {
  const compareFields = [
    'origin.address',
    'origin.pickupDateTime',
    'destination.address',
    'serviceInformation.commodity',
    'serviceInformation.dangerousGoods',
    'serviceInformation.exceptedQuantity',
    'serviceInformation.serviceType',
    'serviceInformation.unNumber',
  ];
  return (
    !isEqual(
      pick(quoteData, compareFields),
      pick(currentShipmentData, compareFields)
    ) ||
    werePackagesChanged(
      quoteData.packageList.packages,
      currentShipmentData.packageList.packages
    )
  );
};

export function useQuoteFormValues({
  forms,
  account,
  setAccount,
  setUnitSystem,
  refreshValues,
  values,
}) {
  const { search } = useLocation();
  const [wasPrefilled, setWasPrefilled] = useState();
  const [warningVisible, setWarningVisible] = useState();

  // Capture the prefilled quote data on the first render
  const queryParams = new URLSearchParams(search);
  const referenceNo = queryParams.get('referenceNo');
  const { data, loading } = useQuery(QUOTE_DETAIL_QUERY, {
    variables: { referenceNo, prepareConversion: true },
    skip: !referenceNo,
  });
  const prefillQuote = data?.getCustomerQuote;
  const qouteAccount = useAccount(prefillQuote?.metadata?.qolAccount?.number);

  const prefillQuoteValues = useCallback(
    omitFields => {
      const newValues = shipmentConversions.quoteToShipmentForm(prefillQuote);
      const withoutOmitted = omit(newValues, omitFields);
      forms[0].form.setFieldsValue(withoutOmitted.origin);
      forms[1].form.setFieldsValue(withoutOmitted.destination);
      forms[2].form.setFieldsValue(withoutOmitted.serviceInformation);

      forms[3].form.resetFields();
      forms[3].form.setFieldsValue(withoutOmitted.packageList);

      forms.forEach(({ formRef }) => {
        if (formRef.current) {
          formRef.current.forceUpdate();
        }
      });
      setUnitSystem(newValues.unitSystem);
      refreshValues();
    },
    [forms, prefillQuote, refreshValues, setUnitSystem]
  );

  const resetQuote = useCallback(() => {
    prefillQuoteValues([
      'origin.contact',
      'origin.companyName',
      'destination.contact',
      'destination.companyName',
    ]);
  }, [prefillQuoteValues]);

  useEffect(() => {
    if (
      !prefillQuote ||
      !account ||
      loading ||
      !forms[0].formRef.current ||
      wasPrefilled
    ) {
      return;
    }

    prefillQuoteValues();
    setWasPrefilled(true);
  }, [
    prefillQuote,
    account,
    loading,
    forms,
    refreshValues,
    setUnitSystem,
    wasPrefilled,
    prefillQuoteValues,
  ]);

  useEffect(() => {
    if (!qouteAccount || account?.number === qouteAccount.number) return;
    setAccount(qouteAccount);
  }, [qouteAccount, account, setAccount]);

  useEffect(() => {
    if (prefillQuote && wasPrefilled) {
      const quoteValues = shipmentConversions.quoteToShipmentForm(prefillQuote);
      setWarningVisible(wasShipmentChanged(quoteValues, values));
    }
  }, [prefillQuote, wasPrefilled, values]);

  useEffect(() => {
    if (!referenceNo) {
      setWarningVisible(false);
    }
  }, [referenceNo]);

  return {
    referenceNo,
    quoteData: prefillQuote,
    loading,
    warningVisible,
    resetValues: resetQuote,
  };
}
